import { useState } from 'react';

export default function CTASection() {
  const [formData, setFormData] = useState({ name: '', phone: '', company: '', comments: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validatePhone = (phone: string): boolean => {
    const cleanPhone = phone.replace(/[^\d+]/g, '');
    const phoneRegex = /^\+?\d{10,15}$/;
    return phoneRegex.test(cleanPhone);
  };

  const normalizePhone = (phone: string): string => {
    let cleanPhone = phone.replace(/[^\d+]/g, '');
    if (!cleanPhone.startsWith('+')) cleanPhone = '+' + cleanPhone;
    return cleanPhone;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) { setErrorMessage('Пожалуйста, укажите ваше имя'); return; }
    if (!formData.phone.trim()) { setErrorMessage('Пожалуйста, укажите ваш телефон'); return; }
    if (!privacyAccepted) { setErrorMessage('Пожалуйста, подтвердите согласие на обработку персональных данных'); return; }
    if (!validatePhone(formData.phone)) { setErrorMessage('Пожалуйста, введите корректный номер телефона (минимум 10 цифр)'); return; }

    setFormStatus('submitting');
    setErrorMessage('');

    try {
      const honeypotValue = (document.querySelector('input[name="company_alt"]') as HTMLInputElement)?.value || '';
      if (honeypotValue.trim()) {
        setFormStatus('success');
        setFormData({ name: '', phone: '', company: '', comments: '' });
        setTimeout(() => setFormStatus('idle'), 5000);
        return;
      }

      const normalizedPhone = normalizePhone(formData.phone);
      const baseUrl = 'https://businesstrainer.bitrix24.by/rest/1/yxbm0u72myjrxywc/crm.lead.add.json';
      const params = new URLSearchParams({
        'fields[NAME]': formData.name,
        'fields[TITLE]': `Лид с businesstraining corporate-academy ${formData.name}`,
        'fields[COMPANY_TITLE]': formData.company,
        'fields[PHONE][0][VALUE]': normalizedPhone,
        'fields[COMMENTS]': formData.comments,
        'params[REGISTER_SONET_EVENT]': 'Y',
        'ASSIGNED_BY_ID': '1'
      });

      const response = await fetch(`${baseUrl}?${params.toString()}`, { method: 'GET' });
      if (response.ok) {
        const result = await response.json();
        if (result.result) {
          setFormStatus('success');
          setFormData({ name: '', phone: '', company: '', comments: '' });
          setTimeout(() => setFormStatus('idle'), 5000);
        } else {
          throw new Error('Ошибка отправки');
        }
      } else {
        throw new Error('Ошибка отправки');
      }
    } catch {
      setFormStatus('error');
      setErrorMessage('Произошла ошибка при отправке. Пожалуйста, попробуйте позже.');
      setTimeout(() => { setFormStatus('idle'); setErrorMessage(''); }, 5000);
    }
  };

  const benefits = [
    { icon: 'ri-search-eye-line', text: 'Диагностика текущей системы обучения' },
    { icon: 'ri-file-list-3-line', text: 'Индивидуальный проект академии' },
    { icon: 'ri-line-chart-line', text: 'Измеримые бизнес-результаты' },
  ];

  return (
    <section id="contact" className="py-20 md:py-28 bg-white">
      <div className="max-w-5xl mx-auto px-6">
        <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-6 sm:p-10 md:p-16">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 md:mb-6">
              Хотите создать корпоративную академию, которая будет развивать сотрудников годами?
            </h2>
            <p className="text-base md:text-lg text-white/90 max-w-3xl mx-auto leading-relaxed mb-8">
              Помогу разработать систему корпоративного обучения, которая станет частью бизнеса: от адаптации новых сотрудников до подготовки руководителей и внутренних экспертов.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
              {benefits.map((benefit, i) => (
                <div key={i} className="flex items-center gap-2 text-white/80">
                  <div className="w-8 h-8 bg-white/15 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${benefit.icon} text-sm text-white`}></i>
                  </div>
                  <span className="text-sm">{benefit.text}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="max-w-2xl mx-auto">
            <form id="academy_contact_form" data-readdy-form onSubmit={handleSubmit} className="bg-white rounded-xl p-5 sm:p-8 shadow-lg">
              <div className="space-y-4 sm:space-y-6">
                <div>
                  <label htmlFor="ac_name" className="block text-sm font-semibold text-gray-900 mb-2">Имя <span className="text-red-500">*</span></label>
                  <input type="text" id="ac_name" name="name" value={formData.name} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all text-base" placeholder="Введите ваше имя" />
                </div>
                <div>
                  <label htmlFor="ac_phone" className="block text-sm font-semibold text-gray-900 mb-2">Телефон <span className="text-red-500">*</span></label>
                  <input type="tel" id="ac_phone" name="phone" value={formData.phone} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all text-base" placeholder="+375 (XX) XXX-XX-XX" />
                </div>
                <div>
                  <label htmlFor="ac_company" className="block text-sm font-semibold text-gray-900 mb-2">Название компании</label>
                  <input type="text" id="ac_company" name="company" value={formData.company} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all text-base" placeholder="Введите название компании" />
                </div>
                <div>
                  <label htmlFor="ac_comments" className="block text-sm font-semibold text-gray-900 mb-2">Запрос</label>
                  <textarea id="ac_comments" name="comments" value={formData.comments} onChange={handleInputChange} maxLength={500} rows={3} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all text-base resize-none" placeholder="Опишите ваш запрос (до 500 символов)" />
                  <p className="text-xs text-gray-500 mt-1">{formData.comments.length}/500 символов</p>
                </div>

                <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="opacity-0 absolute -z-10 pointer-events-none" />

                {errorMessage && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{errorMessage}</div>
                )}
                {formStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm flex items-center"><i className="ri-check-line mr-1.5 text-lg"></i> Спасибо! Ваша заявка успешно отправлена. Мы свяжемся с вами в ближайшее время.</div>
                )}

                <label className="flex items-start gap-3 cursor-pointer group">
                  <input type="checkbox" checked={privacyAccepted} onChange={(e) => setPrivacyAccepted(e.target.checked)} className="mt-0.5 w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-600 cursor-pointer flex-shrink-0" />
                  <span className="text-sm text-gray-600 leading-snug group-hover:text-gray-900 transition-colors">Я согласен(на) на <a href="https://sivko.by/load/privacy-policy.pdf" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700 underline">обработку персональных данных</a></span>
                </label>

                <button type="submit" disabled={formStatus === 'submitting' || !privacyAccepted} className="w-full bg-teal-600 text-white px-4 sm:px-8 py-3 sm:py-4 rounded-lg text-sm sm:text-lg font-semibold hover:bg-teal-700 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none whitespace-nowrap cursor-pointer">
                  {formStatus === 'submitting' ? 'Отправка...' : 'Отправить заявку'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}